// Settings
//
// The app's settings live here, reached from the profile menu.
//
// Appearance used to be a tab inside Edit Profile, which is the wrong home for
// it: editing your profile is about what other people see, and a theme is
// about what YOU see. Anyone looking for a theme switch went to a screen about
// their photos and bio to find it.
//
// Chat room notifications join it, because 058 gave chat rooms a way into the
// notifications feed and people need a way to turn that off. Rate limits are
// not consent.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { backDestination } from '../../utils/navigation/backDestination';
import { DISCOVERY_SECTION_ID } from '../../constants/settingsAnchors';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { ThemeToggle } from '../../components/CommunityChatRooms/ThemeToggle';
import { profileService, NotificationPreferences } from '../../services/Profile/profileService';
import { safetyService } from '../../services/Safety/safetyService';
import { SafetySettings } from '../../types/safety';
import { DiscoverySettings } from '../../components/Profile/types';
import { logger } from '../../utils/logger';
import { useSEO } from '../../hooks';

const Page = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  padding-bottom: 96px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid var(--border-light);
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: ${theme.spacing.xs};
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
`;

const Content = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
`;

const SectionHint = styled.p`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg};
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 12px;
`;

const RowText = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const RowHint = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

/** A plain switch, so this page owns no state the server does not. */
const Switch = styled.button<{ $on: boolean }>`
  flex-shrink: 0;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$on ? theme.colors.primary : 'var(--border-medium)')};
  background: ${props => (props.$on ? theme.colors.primary : 'var(--bg-tertiary)')};
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => (props.$on ? '25px' : '3px')};
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusNote = styled.div<{ $error?: boolean }>`
  font-size: 0.85rem;
  color: ${props => (props.$error ? theme.colors.error : 'var(--text-secondary)')};
  min-height: 1.2em;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid var(--border-medium);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
`;

const Range = styled.input`
  width: 100%;
`;

const Choice = styled.button<{ $on: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: 999px;
  border: 1px solid ${props => (props.$on ? theme.colors.primary : 'var(--border-medium)')};
  background: ${props => (props.$on ? theme.colors.primary : 'transparent')};
  color: ${props => (props.$on ? '#fff' : 'var(--text-primary)')};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const ChoiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  align-self: flex-start;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${props => (props.$danger ? theme.colors.error : 'var(--border-medium)')};
  background: ${props => (props.$danger ? theme.colors.error : 'var(--bg-secondary)')};
  color: ${props => (props.$danger ? '#fff' : 'var(--text-primary)')};
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const LinkRow = styled(Row)`
  cursor: pointer;

  &:hover { background: var(--bg-secondary); }
`;

const GENDER_OPTIONS: DiscoverySettings['genderPreference'] = ['male', 'female', 'non-binary', 'other'];


const SAFETY_ROWS: Array<{ key: keyof SafetySettings; title: string; hint: string }> = [
  { key: 'allowMessagesFromMatches', title: 'Messages from matches', hint: 'People you have matched with can message you.' },
  { key: 'allowMessagesFromPremium', title: 'Messages from Premium members', hint: 'Premium members can message you without matching first.' },
  { key: 'showOnlineStatus', title: 'Show when I am online', hint: 'Others can see that you are active now.' },
  { key: 'shareLocation', title: 'Share my distance', hint: 'Show how far away you are on your profile and in feeds.' },
  { key: 'hideProfileFromBlocked', title: 'Hide me from people I block', hint: 'Someone you block cannot find your profile.' },
];

const NOTIFICATION_ROWS: Array<{
  key: keyof NotificationPreferences;
  title: string;
  hint: string;
}> = [
  {
    key: 'communityReplies',
    title: 'Replies to me',
    hint: 'When someone comments on your post or replies to your comment.',
  },
  {
    key: 'communityThreads',
    title: 'Conversations I joined',
    hint: 'When a thread you have commented in gets new activity. At most one a day per post.',
  },
  {
    key: 'communitySaved',
    title: 'Posts I saved',
    hint: 'When a post you bookmarked gets new comments.',
  },
  {
    key: 'communityMilestones',
    title: 'My post milestones',
    hint: 'When one of your posts reaches 5, 10 or 25 upvotes.',
  },
  {
    key: 'communityDigest',
    title: 'Daily room summary',
    hint: 'One note a day about new posts in the rooms you have joined.',
  },
];

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Where Back goes.
   *
   * Not navigate(-1). Settings links out to Safety, and Safety's own Back
   * comes here, so a history-relative Back lands on Safety and Safety sends
   * you straight back to Settings -- a two-page trap with no way out but the
   * browser's own controls.
   *
   * So Back goes UP, to the page Settings belongs to, rather than backwards
   * through wherever the viewer has been. Openers that are not the profile
   * pass their own origin in location state.
   *
   * Both hops are plain pushes. Replacing would collapse the trail, but it
   * also leaves two identical entries side by side, so the system back gesture
   * spends a press going nowhere. Pushing keeps the browser's back button
   * meaning what it says -- retracing where you actually went, and arriving
   * somewhere eventually -- while the in-app Back always climbs.
   */
  const returnTo = backDestination(location.state);

  /**
   * Arriving from a link that names a section scrolls to it.
   *
   * The deck's "Adjust who you see" is the reason. Dropping someone at the top
   * of a long settings page and leaving them to find the control they were
   * promised is only marginally better than not linking at all.
   */
  useEffect(() => {
    if (location.hash !== `#${DISCOVERY_SECTION_ID}`) return;
    // After paint, so the section exists to scroll to.
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(DISCOVERY_SECTION_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);
  useSEO({
    title: 'Settings | Plenty of Hearts',
    description: 'Manage your appearance and notification preferences.',
    canonical: '/settings',
  });

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState<keyof NotificationPreferences | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [discovery, setDiscovery] = useState<DiscoverySettings | null>(null);
  const [discoveryStatus, setDiscoveryStatus] = useState<string | null>(null);
  const [safety, setSafety] = useState<SafetySettings | null>(null);

  // Account forms. Kept local and cleared after use — a password lingering in
  // React state is a password on the page longer than it needs to be.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');

  useEffect(() => {
    let cancelled = false;
    profileService.getNotificationPreferences()
      .then((next) => { if (!cancelled) setPrefs(next); })
      .catch((err) => {
        logger.error('Failed to load notification preferences:', err);
        if (!cancelled) setError("We couldn't load your notification settings.");
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    profileService.getDiscoverySettings('')
      .then((next) => { if (!cancelled) setDiscovery(next); })
      .catch((err) => logger.error('Failed to load discovery settings:', err));
    safetyService.getSafetySettings()
      .then((next) => { if (!cancelled) setSafety(next); })
      .catch((err) => logger.error('Failed to load safety settings:', err));
    return () => { cancelled = true; };
  }, []);

  /**
   * Discovery settings are saved explicitly rather than on every slider move —
   * dragging an age range would otherwise fire a write per pixel.
   */
  const saveDiscovery = useCallback(async () => {
    if (!discovery) return;
    setDiscoveryStatus('Saving…');
    try {
      await profileService.updateDiscoverySettings('', discovery);
      setDiscoveryStatus('Saved.');
    } catch (err) {
      logger.error('Failed to save discovery settings:', err);
      setDiscoveryStatus("That couldn't be saved. Please try again.");
    }
  }, [discovery]);

  const toggleSafety = useCallback(async (key: keyof SafetySettings) => {
    if (!safety) return;
    const next = { ...safety, [key]: !safety[key] };
    setSafety(next);
    try {
      await safetyService.updateSafetySettings({ [key]: next[key] });
    } catch (err) {
      logger.error('Failed to save safety setting:', err);
      setSafety(safety);
    }
  }, [safety]);

  const toggle = useCallback(async (key: keyof NotificationPreferences) => {
    if (!prefs || saving) return;
    const next = !prefs[key];

    // Optimistic: a switch that waits on a round trip before moving feels
    // broken. Rolled back below if the server disagrees.
    setPrefs({ ...prefs, [key]: next });
    setSaving(key);
    setError(null);
    try {
      const saved = await profileService.updateNotificationPreferences({ [key]: next });
      setPrefs(saved);
    } catch (err) {
      logger.error('Failed to save notification preference:', err);
      setPrefs((current) => (current ? { ...current, [key]: !next } : current));
      setError("That setting couldn't be saved. Please try again.");
    } finally {
      setSaving(null);
    }
  }, [prefs, saving]);

  const handleChangePassword = async () => {
    setAccountError(null);
    setAccountStatus(null);
    setBusy(true);
    try {
      await profileService.changePassword(currentPassword, newPassword);
      setAccountStatus('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      setAccountError(err?.message || 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  const handleChangeEmail = async () => {
    setAccountError(null);
    setAccountStatus(null);
    setBusy(true);
    try {
      await profileService.changeEmail(currentPassword, newEmail);
      setAccountStatus(`Email updated to ${newEmail}.`);
      setCurrentPassword('');
      setNewEmail('');
    } catch (err: any) {
      setAccountError(err?.message || 'Could not change your email.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    setAccountError(null);
    setBusy(true);
    try {
      await profileService.deleteAccount(currentPassword);
      // The account is gone; there is nothing left to return to.
      window.location.href = '/';
    } catch (err: any) {
      setAccountError(err?.message || 'Could not delete your account.');
      setBusy(false);
    }
  };

  return (
    <Page>
      <Header>
        <BackButton onClick={() => navigate(returnTo)}>← Back</BackButton>
        <Title>Settings</Title>
      </Header>

      <Content>
        <Section>
          <SectionTitle>Appearance</SectionTitle>
          <SectionHint>
            Choose your preferred theme. This affects what you see, not what
            anyone else does.
          </SectionHint>
          <Row>
            <RowText>
              <RowTitle>Theme</RowTitle>
              <RowHint>Light or dark, across the app.</RowHint>
            </RowText>
            <ThemeToggle />
          </Row>
        </Section>

        <Section>
          <SectionTitle>Chat room notifications</SectionTitle>
          <SectionHint>
            These appear in the Notifications tab of your messages. Turning one
            off stops it being created — you will not find them hidden
            somewhere later.
          </SectionHint>

          {NOTIFICATION_ROWS.map((row) => (
            <Row key={row.key}>
              <RowText>
                <RowTitle>{row.title}</RowTitle>
                <RowHint>{row.hint}</RowHint>
              </RowText>
              <Switch
                type="button"
                role="switch"
                aria-checked={prefs ? prefs[row.key] : false}
                aria-label={row.title}
                $on={prefs ? prefs[row.key] : false}
                disabled={!prefs || saving !== null}
                onClick={() => toggle(row.key)}
              />
            </Row>
          ))}

          <StatusNote $error={!!error}>
            {error || (prefs ? '' : 'Loading your settings…')}
          </StatusNote>
        </Section>

        <Section id={DISCOVERY_SECTION_ID}>
          <SectionTitle>Who you see</SectionTitle>
          <SectionHint>
            These decide who appears in your deck and feeds. Until now they
            could only be set during onboarding — anyone who skipped that step
            was being shown everyone.
          </SectionHint>

          {discovery && (
            <Row style={{ flexDirection: 'column', alignItems: 'stretch', gap: theme.spacing.lg }}>
              <Field>
                <Label>Show me</Label>
                <ChoiceRow>
                  {GENDER_OPTIONS.map((option) => {
                    const on = discovery.genderPreference.includes(option);
                    return (
                      <Choice
                        key={option}
                        type="button"
                        $on={on}
                        onClick={() => setDiscovery({
                          ...discovery,
                          genderPreference: on
                            ? discovery.genderPreference.filter((g) => g !== option)
                            : [...discovery.genderPreference, option],
                        })}
                      >
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </Choice>
                    );
                  })}
                </ChoiceRow>
                <RowHint>
                  Leave all off to see everyone.
                </RowHint>
              </Field>

              <Field>
                <Label>Age range: {discovery.ageRange.min} – {discovery.ageRange.max}</Label>
                <Range
                  type="range" min={18} max={99}
                  value={discovery.ageRange.min}
                  onChange={(e) => setDiscovery({
                    ...discovery,
                    ageRange: {
                      ...discovery.ageRange,
                      min: Math.min(Number(e.target.value), discovery.ageRange.max),
                    },
                  })}
                />
                <Range
                  type="range" min={18} max={99}
                  value={discovery.ageRange.max}
                  onChange={(e) => setDiscovery({
                    ...discovery,
                    ageRange: {
                      ...discovery.ageRange,
                      max: Math.max(Number(e.target.value), discovery.ageRange.min),
                    },
                  })}
                />
              </Field>

              <Field>
                <Label>Maximum distance: {discovery.distance} miles</Label>
                <Range
                  type="range" min={1} max={500}
                  value={discovery.distance}
                  onChange={(e) => setDiscovery({ ...discovery, distance: Number(e.target.value) })}
                />
              </Field>

              <ActionButton type="button" onClick={saveDiscovery}>Save</ActionButton>
              <StatusNote>{discoveryStatus}</StatusNote>
            </Row>
          )}
        </Section>

        <Section>
          <SectionTitle>Privacy and safety</SectionTitle>
          <SectionHint>
            Who can reach you, and what other people can see about you.
          </SectionHint>

          {SAFETY_ROWS.map((row) => (
            <Row key={row.key}>
              <RowText>
                <RowTitle>{row.title}</RowTitle>
                <RowHint>{row.hint}</RowHint>
              </RowText>
              <Switch
                type="button"
                role="switch"
                aria-checked={safety ? safety[row.key] : false}
                aria-label={row.title}
                $on={safety ? safety[row.key] : false}
                disabled={!safety}
                onClick={() => toggleSafety(row.key)}
              />
            </Row>
          ))}

          <LinkRow onClick={() => navigate('/safety')}>
            <RowText>
              <RowTitle>Blocked people and reports</RowTitle>
              <RowHint>Review who you have blocked, and the reports you have made.</RowHint>
            </RowText>
            <span aria-hidden>›</span>
          </LinkRow>
        </Section>

        <Section>
          <SectionTitle>Account</SectionTitle>
          <SectionHint>
            Your current password is required for each of these — a signed-in
            session is not proof of identity on a borrowed phone.
          </SectionHint>

          <Row style={{ flexDirection: 'column', alignItems: 'stretch', gap: theme.spacing.md }}>
            <Field>
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Field>

            <Field>
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <ActionButton
                type="button"
                disabled={busy || !currentPassword || newPassword.length < 8}
                onClick={handleChangePassword}
              >
                Change password
              </ActionButton>
            </Field>

            <Field>
              <Label htmlFor="new-email">New email address</Label>
              <Input
                id="new-email"
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <ActionButton
                type="button"
                disabled={busy || !currentPassword || !newEmail.includes('@')}
                onClick={handleChangeEmail}
              >
                Change email
              </ActionButton>
            </Field>

            {accountStatus && <StatusNote>{accountStatus}</StatusNote>}
            {accountError && <StatusNote $error>{accountError}</StatusNote>}
          </Row>

          <Row style={{ flexDirection: 'column', alignItems: 'stretch', gap: theme.spacing.md }}>
            <RowText>
              <RowTitle>Delete my account</RowTitle>
              <RowHint>
                This removes your profile, photos, messages, matches and chat
                room activity. It cannot be undone, and nothing is kept.
              </RowHint>
            </RowText>
            <Field>
              <Label htmlFor="confirm-delete">Type DELETE to confirm</Label>
              <Input
                id="confirm-delete"
                value={confirmDelete}
                onChange={(e) => setConfirmDelete(e.target.value)}
                placeholder="DELETE"
              />
            </Field>
            <ActionButton
              type="button"
              $danger
              disabled={busy || confirmDelete !== 'DELETE' || !currentPassword}
              onClick={handleDeleteAccount}
            >
              Delete my account permanently
            </ActionButton>
          </Row>
        </Section>
      </Content>
    </Page>
  );
};

export default Settings;
